import React, { useCallback, useEffect, useState } from 'react'
import { useController } from 'react-hook-form'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import InputMask from 'react-input-mask'

import styles from './HFInputs.module.sass'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'

function PostalCodeInput(props) {
	const { mask, onChange, ...attributes } = props
	return (
		<InputMask {...attributes} mask={mask} onChange={onChange}></InputMask>
	)
}

const HFValidatePostalCodeInput = (props) => {
	const { name, inputMask, type, placeholder, initialValue, ...attributes } =
		props
	const { i18n, expireSession } = useGlobal()
	const [results, setResults] = useState([])
	const [postalCodeQuery, setPostalCodeQuery] = useState('')
	const [isValidPostalCode, setIsValidPostalCode] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const postalCodeRegex = /^\d{4}-\d{3}$/

	const labels = {
		invalidPostalCode: i18n`Invalid postal code!`,
	}

	const {
		field: { ref, value, ...field },
	} = useController({ name })

	const handlePostalCode = useCallback((event) => {
		if (postalCodeQuery !== placeholder) {
			setPostalCodeQuery(event.target.value)
			field.onChange(event.target.value)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (value) {
			setPostalCodeQuery(value)
		}
	}, [value])

	useEffect(() => {
		if (attributes.disabled && !value) {
			setPostalCodeQuery('')
		}
	}, [attributes.disabled, value])

	useEffect(() => {
		if (!postalCodeQuery?.match(postalCodeRegex)) {
			setResults([])
			setIsValidPostalCode(null)
			return
		}

		const controller = new AbortController()
		setIsLoading(true)
		fetch(
			`${API_URL}/api/metadata/valid_postal_code?postalCode=${postalCodeQuery}`,
			fetchOptions({
				signal: controller.signal,
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				setResults(r)
				setIsLoading(false)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				}
				setResults([])
				setIsLoading(false)
			})

		return () => {
			controller.abort()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expireSession, postalCodeQuery])
	const currentPostalCodeObject = results.find(
		(element) => element?.postalCode === postalCodeQuery
	)

	useEffect(() => {
		if (!postalCodeQuery?.match(postalCodeRegex)) {
			setIsValidPostalCode(null)
			return
		}

		if (currentPostalCodeObject?.postalCode === postalCodeQuery) {
			setIsValidPostalCode(true)
			field.onChange(postalCodeQuery)
		} else {
			setIsValidPostalCode(false)
			field.onChange()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPostalCodeObject?.postalCode, postalCodeQuery])

	return (
		<HStack className={`${styles['field-validation-input']}`}>
			<PostalCodeInput
				{...attributes}
				{...field}
				value={value || ''}
				type={type}
				mask={inputMask}
				placeholder={placeholder}
				onChange={handlePostalCode}
			/>

			<HStack className={styles['county-and-district']}>
				<strong>
					{!isLoading && isValidPostalCode === false && (
						<small
							className={`${styles['pill']} ${styles['--error']}`}
						>
							{labels.invalidPostalCode}
						</small>
					)}
					{!isLoading && isValidPostalCode && (
						<HStack className={styles['pills-container']}>
							<small
								className={`${styles['pill']} ${styles['--county']}`}
							>
								{currentPostalCodeObject?.county?.description}
							</small>
							<small
								className={`${styles['pill']} ${styles['--district']}`}
							>
								{
									currentPostalCodeObject?.county?.district
										?.name
								}
							</small>
						</HStack>
					)}
				</strong>
			</HStack>
		</HStack>
	)
}

export default HFValidatePostalCodeInput
